/**
 * API Endpoints Configuration
 * Centralized endpoint definitions for the application
 */

// Base paths
const AUTH_BASE = '/api/auth'
const CLIENTS_BASE = '/api/clients'
const FOURNISSEURS_BASE = '/api/fournisseurs'
const PRODUITS_BASE = '/api/produits'
const FACTURES_BASE = '/api/factures'
const STOCK_BASE = '/api/stock'
const INVENTORY_PRODUCTS_BASE = '/api/stock/produits'
const WAREHOUSES_BASE = '/api/stock/warehouses'
const CATEGORIES_BASE = '/api/stock/categories'
const STOCK_ALERTS_BASE = '/api/stock/alerts'
const INVENTORY_ANALYTICS_BASE = '/api/inventory/analytics'
const INVENTORY_TRANSFERS_BASE = '/api/inventory/transfers'
const INVENTORY_LABELS_BASE = '/api/inventory/products'
// Reporting base paths
// Note:
// - `/api/rapports` is used for the aggregated dashboard payload
// - `/api/reports` is used for the detailed reporting/exports APIs
const REPORTS_DASHBOARD_BASE = '/api/rapports'
const REPORTS_BASE = '/api/reports'
const AI_BASE = '/api/ai'
const USERS_BASE = '/api/users'

// Authentication endpoints
export const AUTH_ENDPOINTS = {
  LOGIN: `${AUTH_BASE}/login`,
  REGISTER: `${AUTH_BASE}/register`,
  LOGOUT: `${AUTH_BASE}/logout`,
  REFRESH: `${AUTH_BASE}/refresh`,
  PROFILE: `${AUTH_BASE}/me`, // ✅ Corrigé: correspond à l'endpoint backend /me
  UPDATE_PROFILE: `${AUTH_BASE}/profile`,
  CHANGE_PASSWORD: `${AUTH_BASE}/change-password`,
  FORGOT_PASSWORD: `${AUTH_BASE}/forgot-password`,
  RESET_PASSWORD: `${AUTH_BASE}/reset-password`,
  VERIFY_EMAIL: `${AUTH_BASE}/verify-email`,
  API_TOKENS: `${AUTH_BASE}/api-tokens`,
}

// Settings endpoints
export const SETTINGS_ENDPOINTS = {
  COMPANY: '/api/settings/company',
  COMPANY_LOGO: '/api/settings/company/logo',
}

// Client endpoints
export const CLIENT_ENDPOINTS = {
  LIST: CLIENTS_BASE,
  CREATE: CLIENTS_BASE,
  GET: id => `${CLIENTS_BASE}/${id}`,
  UPDATE: id => `${CLIENTS_BASE}/${id}`,
  DELETE: id => `${CLIENTS_BASE}/${id}`,
  RESTORE: id => `${CLIENTS_BASE}/${id}/restore`,
  SEARCH: `${CLIENTS_BASE}/search`,
  STATS: id => `${CLIENTS_BASE}/${id}/stats`,
  FACTURES: id => `${CLIENTS_BASE}/${id}/factures`,
  EXPORT: `${CLIENTS_BASE}/export`,
  PRODUCT_PRICES: (clientId, params = {}) => {
    const queryParams = new URLSearchParams()
    if (params.produitId) queryParams.append('produitId', params.produitId)
    if (params.dateDebut) queryParams.append('dateDebut', params.dateDebut)
    if (params.dateFin) queryParams.append('dateFin', params.dateFin)
    if (params.summary) queryParams.append('summary', params.summary)
    const queryString = queryParams.toString()
    return `/api/clients/${clientId}/product-prices${queryString ? `?${queryString}` : ''}`
  },
}

// Supplier endpoints
export const FOURNISSEUR_ENDPOINTS = {
  LIST: FOURNISSEURS_BASE,
  CREATE: FOURNISSEURS_BASE,
  GET: id => `${FOURNISSEURS_BASE}/${id}`,
  UPDATE: id => `${FOURNISSEURS_BASE}/${id}`,
  DELETE: id => `${FOURNISSEURS_BASE}/${id}`,
  RESTORE: id => `${FOURNISSEURS_BASE}/${id}/restore`,
  SEARCH: `${FOURNISSEURS_BASE}/search`,
  ACTIFS: `${FOURNISSEURS_BASE}/actifs`,
  COUNT: `${FOURNISSEURS_BASE}/count`,
}

// Product endpoints
export const PRODUIT_ENDPOINTS = {
  LIST: PRODUITS_BASE,
  CREATE: PRODUITS_BASE,
  GET: id => `${PRODUITS_BASE}/${id}`,
  UPDATE: id => `${PRODUITS_BASE}/${id}`,
  DELETE: id => `${PRODUITS_BASE}/${id}`,
  SEARCH: `${PRODUITS_BASE}/search`,
  CATEGORIES: `${PRODUITS_BASE}/categories`,
  LOW_STOCK: `${PRODUITS_BASE}/low-stock`,
  UPLOAD_IMAGE: id => `${PRODUITS_BASE}/${id}/image`,
  EXPORT: `${PRODUITS_BASE}/export`,
}

// Invoice endpoints
export const FACTURE_ENDPOINTS = {
  LIST: FACTURES_BASE,
  CREATE: FACTURES_BASE,
  GET: id => `${FACTURES_BASE}/${id}`,
  UPDATE: id => `${FACTURES_BASE}/${id}`,
  DELETE: id => `${FACTURES_BASE}/${id}`,
  SEARCH: `${FACTURES_BASE}/search`,
  VALIDATE: id => `${FACTURES_BASE}/${id}/validate`,
  CANCEL: id => `${FACTURES_BASE}/${id}/cancel`,
  GENERATE_PDF: id => `${FACTURES_BASE}/${id}/pdf`,
  SEND_EMAIL: id => `${FACTURES_BASE}/${id}/send`,
  STATS: `${FACTURES_BASE}/stats`,
  OVERDUE: `${FACTURES_BASE}/overdue`,
  EXPORT: `${FACTURES_BASE}/export`,
}

// Stock endpoints
export const STOCK_ENDPOINTS = {
  DETAIL: id => `${STOCK_BASE}/${id}`,
  MOVEMENTS: id => `${STOCK_BASE}/${id}/mouvements`,
  CREATE_MOVEMENT: `${STOCK_BASE}/mouvement`,
  RESERVE: `${STOCK_BASE}/reserve`,
  COMMIT: `${STOCK_BASE}/commit`,
  RELEASE: `${STOCK_BASE}/release`,
  ADJUST: id => `${STOCK_BASE}/${id}/adjust`,
  RESERVATIONS_BY_REFERENCE: reference => `${STOCK_BASE}/reservations/${reference}`,
}

export const INVENTORY_PRODUCT_ENDPOINTS = {
  LIST: INVENTORY_PRODUCTS_BASE,
  DETAIL: id => `${INVENTORY_PRODUCTS_BASE}/${id}`,
  BY_REFERENCE: reference => `${INVENTORY_PRODUCTS_BASE}/reference/${reference}`,
  CREATE: INVENTORY_PRODUCTS_BASE,
  UPDATE: id => `${INVENTORY_PRODUCTS_BASE}/${id}`,
  DELETE: id => `${INVENTORY_PRODUCTS_BASE}/${id}`,
}

export const WAREHOUSE_ENDPOINTS = {
  LIST: WAREHOUSES_BASE,
  LIST_ALL: `${WAREHOUSES_BASE}/all`,
  DEFAULT: `${WAREHOUSES_BASE}/default`,
  DETAIL: id => `${WAREHOUSES_BASE}/${id}`,
  CREATE: WAREHOUSES_BASE,
  UPDATE: id => `${WAREHOUSES_BASE}/${id}`,
  SET_DEFAULT: id => `${WAREHOUSES_BASE}/${id}/default`,
  DELETE: id => `${WAREHOUSES_BASE}/${id}`,
}

export const CATEGORY_ENDPOINTS = {
  LIST: CATEGORIES_BASE,
  LIST_ALL: `${CATEGORIES_BASE}/all`,
  DETAIL: id => `${CATEGORIES_BASE}/${id}`,
  CREATE: CATEGORIES_BASE,
  UPDATE: id => `${CATEGORIES_BASE}/${id}`,
  DELETE: id => `${CATEGORIES_BASE}/${id}`,
}

export const STOCK_ALERT_ENDPOINTS = {
  LIST: STOCK_ALERTS_BASE,
  BY_PRODUCT: productId => `${STOCK_ALERTS_BASE}/product/${productId}`,
}

export const INVENTORY_ANALYTICS_ENDPOINTS = {
  MARGIN: `${INVENTORY_ANALYTICS_BASE}/margin`,
  VALUATION: `${INVENTORY_ANALYTICS_BASE}/valuation`,
  ROTATION: `${INVENTORY_ANALYTICS_BASE}/rotation`,
}

export const INVENTORY_TRANSFER_ENDPOINT = INVENTORY_TRANSFERS_BASE

export const BARCODE_ENDPOINTS = {
  LABEL: (productId, params = {}) =>
    `${INVENTORY_LABELS_BASE}/${productId}/label${buildQueryString(params)}`,
}

// Report endpoints
export const REPORT_ENDPOINTS = {
  // Global dashboard data (used on the home dashboard)
  DASHBOARD: `${REPORTS_DASHBOARD_BASE}/dashboard`,

  // Detailed JSON reports
  SALES_SUMMARY: `${REPORTS_BASE}/sales/summary`,
  SALES_KPIS: `${REPORTS_BASE}/sales/kpis`,
  TOP_PRODUCTS: `${REPORTS_BASE}/products/top`,
  TOP_CLIENTS: `${REPORTS_BASE}/clients/top`,
  STOCK_ALERTS: `${REPORTS_BASE}/stock/alerts`,
  CAISSE_SUMMARY: `${REPORTS_BASE}/caisse/summary`,
  CAISSE_KPIS: `${REPORTS_BASE}/caisse/kpis`,
  CAISSE_TOP_PRODUCTS: `${REPORTS_BASE}/caisse/products/top`,

  // File exports (CSV / PDF)
  EXPORT: `${REPORTS_BASE}/export`,
  DOWNLOAD: reportId => `${REPORTS_BASE}/${reportId}/download`,
}

// AI endpoints
export const AI_ENDPOINTS = {
  CHAT: `${AI_BASE}/chat`,
  RECOMMENDATIONS: `${AI_BASE}/recommendations`,
  PREDICT_SALES: `${AI_BASE}/predict-sales`,
  ANALYZE_CUSTOMER: `${AI_BASE}/analyze-customer`,
  SUGGESTIONS: `${AI_BASE}/suggestions`,
}

// User management endpoints
export const USER_ENDPOINTS = {
  LIST: USERS_BASE,
  CREATE: USERS_BASE,
  GET: id => `${USERS_BASE}/${id}`,
  UPDATE: id => `${USERS_BASE}/${id}`,
  DELETE: id => `${USERS_BASE}/${id}`,
  ROLES: `${USERS_BASE}/roles`,
  PERMISSIONS: `${USERS_BASE}/permissions`,
}

// Caisse (POS) endpoints
const CAISSE_BASE = '/api/caisse'
export const CAISSE_ENDPOINTS = {
  // Points
  POINTS: `${CAISSE_BASE}/points`,
  POINT: id => `${CAISSE_BASE}/points/${id}`,
  
  // Sessions
  OPEN_SESSION: `${CAISSE_BASE}/sessions/open`,
  CLOSE_SESSION: id => `${CAISSE_BASE}/sessions/${id}/close`,
  CURRENT_SESSION: `${CAISSE_BASE}/sessions/current`,
  SESSIONS: `${CAISSE_BASE}/sessions`,
  
  // Mouvements
  MOUVEMENTS: `${CAISSE_BASE}/mouvements`,
  
  // Ventes
  VENTES: `${CAISSE_BASE}/ventes`,
  TICKET: id => `${CAISSE_BASE}/ventes/${id}/ticket`,
  
  // Paiements factures
  PAY_FACTURE: factureId => `${CAISSE_BASE}/factures/${factureId}/paiements`,
  
  // Rapports
  X_REPORT: `${CAISSE_BASE}/reports/x`,
  Z_REPORT: `${CAISSE_BASE}/reports/z`,
}

// Upload endpoints
export const UPLOAD_ENDPOINTS = {
  IMAGE: '/api/upload/image',
  DOCUMENT: '/api/upload/document',
  AVATAR: '/api/upload/avatar',
}

// Utility function to build query string
export const buildQueryString = params => {
  const query = new URLSearchParams()
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(v => query.append(key, v))
      } else {
        query.append(key, value)
      }
    }
  })
  
  return query.toString() ? `?${query.toString()}` : ''
}

// Helper to create paginated endpoint
export const paginatedEndpoint = (base, params = {}) => {
  const { page = 0, size = 10, sortBy, sortDirection = 'asc', ...filters } = params
  
  const queryParams = {
    page,
    size,
    ...(sortBy && { sortBy, sortDirection }),
    ...filters,
  }
  
  return `${base}${buildQueryString(queryParams)}`
}

// Helper to create search endpoint
export const searchEndpoint = (base, query, params = {}) => {
  return paginatedEndpoint(base, { query, ...params })
}


